import * as React from 'react';
import { CalendarIcon, X } from 'lucide-react';
import type { Matcher } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDisplayDate, formatISODate, parseISODate } from '@/lib/dateValue';

export interface DateFieldProps {
  /** `YYYY-MM-DD`, matching what `<input type="date">` produced. */
  value: string | null | undefined;
  /** Receives `YYYY-MM-DD`, or `''` when cleared. */
  onValueChange: (value: string) => void;
  /** Earliest selectable date, `YYYY-MM-DD` (the old `min`). */
  min?: string;
  /** Latest selectable date, `YYYY-MM-DD` (the old `max`). */
  max?: string;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Accessible name when there is no associated <Label htmlFor>. */
  'aria-label'?: string;
  /**
   * Month/year dropdowns instead of chevrons. Essential for dates far from
   * today — a date of birth is ~250 chevron taps away.
   */
  captionLayout?: 'buttons' | 'dropdown';
  /** Year range for the dropdown layout. Defaults to min/max, else ±100y. */
  fromYear?: number;
  toYear?: number;
}

/**
 * Branded replacement for `<input type="date">`.
 *
 * The native control renders the operating system's picker: iOS system blue,
 * a completely different control on Android, and another on desktop Chrome.
 * It cannot be themed, so the app had two unrelated date experiences (20
 * native inputs vs 6 uses of our own Calendar). This is the one experience,
 * in Kanfy colours, identical everywhere.
 *
 * The value contract is deliberately unchanged — `YYYY-MM-DD` in, the same
 * out — so call sites swap `<Input type="date" value onChange>` for
 * `<DateField value onValueChange>` with no data-layer changes.
 */
export function DateField({
  value,
  onValueChange,
  min,
  max,
  id,
  disabled,
  placeholder = 'Select date',
  className,
  captionLayout = 'buttons',
  fromYear,
  toYear,
  ...rest
}: DateFieldProps) {
  const [open, setOpen] = React.useState(false);
  const selected = parseISODate(value);
  const minDate = parseISODate(min);
  const maxDate = parseISODate(max);

  // Two separate matchers rather than one object with optional keys —
  // react-day-picker's Matcher union accepts `{before}` or `{after}`, but
  // not a single object whose keys may be undefined.
  const disabledMatcher = React.useMemo(() => {
    const rules: Matcher[] = [];
    if (minDate) rules.push({ before: minDate });
    if (maxDate) rules.push({ after: maxDate });
    return rules.length ? rules : undefined;
  }, [minDate, maxDate]);

  const label = formatDisplayDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={rest['aria-label']}
          className={cn(
            'w-full justify-start gap-2 font-normal h-10 px-3',
            !label && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 opacity-70" aria-hidden="true" />
          <span className="truncate">{label || placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 shadow-e3" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? maxDate ?? undefined}
          onSelect={(date) => {
            // `onSelect` fires with undefined when the selected day is
            // re-clicked; treat that as clearing rather than ignoring it.
            onValueChange(date ? formatISODate(date) : '');
            if (date) setOpen(false);
          }}
          disabled={disabledMatcher}
          captionLayout={captionLayout}
          fromYear={fromYear ?? (minDate ? minDate.getFullYear() : new Date().getFullYear() - 100)}
          toYear={toYear ?? (maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 10)}
          initialFocus
        />
        {/* Reset mirrors the reference picker's affordance, and restores
            something the native input gave for free: a way back to empty. */}
        <div className="flex items-center justify-between border-t border-border p-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => {
              onValueChange('');
              setOpen(false);
            }}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Reset
          </Button>
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
